/**
 * QorTrace — i18n bootstrap (Iter249)
 *
 * Initializes react-i18next with:
 *   - Locales loaded lazily from /locales/<lang>/common.json
 *   - Detection order: cookie → URL `?lang` → URL path /de/, /fr/, ... →
 *     browser Accept-Language → server-provided GeoIP hint → English fallback
 *   - Persistence: cookie + localStorage so we respect the user's last pick
 *   - Supported: en, de, fr, es, ja
 *
 * The runtime language is decided BEFORE React renders so there's no
 * English flash before the chosen locale loads.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";

export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English",  native: "English",   flag: "🇺🇸" },
  { code: "de", name: "German",   native: "Deutsch",   flag: "🇩🇪" },
  { code: "fr", name: "French",   native: "Français",  flag: "🇫🇷" },
  { code: "es", name: "Spanish",  native: "Español",   flag: "🇪🇸" },
  { code: "ja", name: "Japanese", native: "日本語",      flag: "🇯🇵" },
];

export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

/**
 * Iter250 — URL-based locale routing.
 *
 * Inspect the current pathname; if its first segment matches a non-English
 * supported locale (e.g. `/de/...`, `/fr/scan`), return that locale code
 * and the basename to mount BrowserRouter under. English is always served
 * at the root with no prefix so we don't break years of indexed canonical
 * URLs.
 *
 * Returns: { lang, basename }
 *   lang     — the matched ISO code (or null if no match)
 *   basename — the basename to pass to <BrowserRouter> (or "" for root)
 */
export function resolveLocaleFromPath() {
  if (typeof window === "undefined") return { lang: null, basename: "" };
  const segments = window.location.pathname.split("/").filter(Boolean);
  const first = (segments[0] || "").toLowerCase();
  if (SUPPORTED_LANGUAGE_CODES.includes(first) && first !== "en") {
    return { lang: first, basename: `/${first}` };
  }
  return { lang: null, basename: "" };
}

/**
 * Build a localised path for an internal route by prefixing the basename
 * for non-English locales. Used by <LanguageSwitcher> so picking French
 * from `/scan` navigates to `/fr/scan` (and we keep the basename intact
 * for subsequent route changes).
 */
export function withLocalePath(targetLang, path) {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (!targetLang || targetLang === "en") return clean;
  if (!SUPPORTED_LANGUAGE_CODES.includes(targetLang)) return clean;
  return `/${targetLang}${clean}`;
}

const detectorOptions = {
  // Priority: cookie → query string → URL path → browser → htmlTag → fallback
  order: ["cookie", "querystring", "path", "navigator", "htmlTag"],
  // Where to persist the user's choice
  caches: ["cookie", "localStorage"],
  cookieMinutes: 60 * 24 * 365,   // 1 year
  cookieDomain: window.location.hostname,
  // /en/, /de/, /fr/, /es/, /ja/ — first path segment
  lookupFromPathIndex: 0,
  lookupQuerystring: "lang",
  lookupCookie: "qt_lang",
  lookupLocalStorage: "qt_lang",
};

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: SUPPORTED_LANGUAGE_CODES,
    fallbackLng: "en",
    load: "languageOnly",       // ignore `en-US`, just use `en`
    ns: ["common"],
    defaultNS: "common",
    detection: detectorOptions,
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },
    interpolation: {
      escapeValue: false,        // React already escapes
    },
    react: {
      useSuspense: false,         // we manage loading via top-level state
    },
    returnEmptyString: false,    // empty string = fall back to key, not blank
  });

// Keep <html lang="..."> in sync with the resolved language so SEO bots
// (Google + Bing) and screen readers see the right locale.
i18n.on("languageChanged", (lng) => {
  try {
    const short = (lng || "en").split("-")[0];
    document.documentElement.setAttribute("lang", short);
  } catch (_) { /* SSR / no DOM */ }
});

// Fire once on initial load (LanguageDetector resolves AFTER init).
try {
  const initial = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  document.documentElement.setAttribute("lang", initial);
} catch (_) { /* no-op */ }

export default i18n;
